import { ETextureOrigin, IAssetTexture, IProductTexture, ITexture } from '../../@types/ContextProviderRelated';
import { EAssetType } from '../../@types/FilesAPIModels';
import AttributeModel from '../../models/standard/AttributeModel';

// Helper to serialize an array of AttributeModels into plain objects
const serializeAttributes = (attrs?: AttributeModel[]) => {
  return attrs?.map((attr) => ({
    id: attr.id,
    key: attr.key,
    value: attr.value,
    predecessorId: attr.predecessorId,
    type: attr.type,
    unit: attr.unit,
    hidden: attr.hidden,
    forced: attr.forced,
    ownerId: attr.ownerId,
    userId: attr.userId,
    publicFlag: attr.publicFlag,
    localization: attr.localization,
    defaults: attr.defaults,
    validation: attr.validation,
    unitFamily: attr.unitFamily,
  })) || [];
};

type ISerializableAssetTexture = Omit<IAssetTexture, 'material'> & { material: null };

// FIX: Removed 'objects' from Omit so they are included in the export type
type ISerializableProductTexture = Omit<IProductTexture, 'material'> & { 
  material: null;
  country?: string; 
};

export type ISerializableTexture = ISerializableAssetTexture | ISerializableProductTexture;

export const serializeTexture = (texture: ITexture | null | undefined): ISerializableTexture | null => {
  if (!texture) return null;

  // legacy / non existant (missing origin OR dummy id === -1): lets serialize it as ASSET COLOR
  if (!texture.textureOrigin || (texture as ITexture).id === -1) {
    const legacy = texture as Partial<ITexture> & { id?: number };
    return {
      textureOrigin: ETextureOrigin.ASSET,
      id: legacy.id ?? -1,
      name: legacy.name ?? '',
      type: (legacy.type ?? EAssetType.COLOR) as EAssetType,
      color: legacy.color,
      rotation: legacy.rotation ?? 0,
      offset: legacy.offset ?? 0,
      material: null,
    } as ISerializableAssetTexture;
  }

  const base = {
    name: texture.name ?? '',
    type: texture.type,
    rotation: texture.rotation ?? 0,
    offset: texture.offset ?? 0,
    color: texture.type === EAssetType.COLOR ? texture.color : undefined,
    material: null as null,
  };

  /* eslint-disable-next-line default-case */
  switch (texture.textureOrigin) {
    case ETextureOrigin.PRODUCT: {
      const t = texture as IProductTexture;
      return {
        ...base,
        textureOrigin: ETextureOrigin.PRODUCT,
        id: t.id,
        assetId: t.assetId,
        country: t.country, 
        // Serialize Product Attributes
        attributes: serializeAttributes(t.attributes),
        // FIX: Serialize Objects (Categories) and their nested Attributes
        objects: t.objects?.map((obj) => ({
            id: obj.id,
            name: obj.name,
            // Access extended properties if they exist on the object interface
            localization: (obj as any).localization,
            ifcObject: (obj as any).ifcObject,
            attributes: serializeAttributes(obj.attributes)
        })),
      } as ISerializableProductTexture;
    }
    case ETextureOrigin.ASSET: {
      const t = texture as IAssetTexture;
      return {
        ...base,
        textureOrigin: ETextureOrigin.ASSET,
        id: t.id,
      } as ISerializableAssetTexture;
    }
  }
};